<script setup lang="ts">
import { ref, watch, onBeforeUnmount, nextTick, onMounted } from 'vue';
import { useI18n } from '@/plugins/i18n';
import { apiSuggestStandards } from '@/components/Search/api.ts';
import type { SearchFilterOption } from '@/components/Search/types.ts';
import iconDocument from '@/assets/img/icon-document.svg';
import iconClose from '@/assets/img/icon-search-clear.svg';

const props = withDefaults(defineProps<{
  modelValue: string | null | undefined;
  placeholder: string;
  // When true, size the dropdown to the main search bar container instead of just the input.
  useSearchBarWidth?: boolean;
  // When true, match the dropdown width to the input element exactly.
  matchInputWidth?: boolean;
  inputClass?: string;
  // When false, position overlay relative to the input container (for modals).
  useFixedOverlay?: boolean;
  // When true, invert the clear icon on blur (useful on dark backgrounds like the main bar).
  invertClearOnBlur?: boolean;
  // Optional: add or subtract px to the dropdown width.
  dropdownExtraWidth?: number;
  // For non-fixed overlays, control how we size/anchor the dropdown.
  overlayPlacement?: 'relative' | 'full';
}>(), {
  useSearchBarWidth: false,
  matchInputWidth: false,
  inputClass: 'crossref-input',
  useFixedOverlay: true,
  invertClearOnBlur: true,
  dropdownExtraWidth: 0,
  overlayPlacement: 'relative',
});

const emit = defineEmits<{
  (e: 'update:modelValue', val: string): void;
  (e: 'select', val: string): void;
}>();

const i18nInstance: any = useI18n();
const { t } = i18nInstance;

const suggestions = ref<SearchFilterOption[]>([]);
const open = ref(false);
const loading = ref(false);
let abortController: AbortController | null = null;
const overlayStyle = ref<Record<string, string>>({});
const inputEl = ref<HTMLElement | null>(null);
const isInputFocused = ref(false);

const minChars = 3;
const baseInputClass =
  'w-full rounded-full border border-[var(--Grey-40,#EFF0F2)] bg-white px-4 py-2 pr-10 text-sm leading-[22px] text-[var(--Dark-Grey,#4E4F52)] focus:outline-none focus:ring-2 focus:ring-[var(--I2I-Gold,#FFBD35)]';
const clearBtnClass =
  'absolute right-3 flex items-center justify-center text-[var(--Dark-Grey,#4E4F52)] opacity-70 hover:opacity-100';

const positionOverlay = () => {
  if (!inputEl.value) return;
  const inputRect = inputEl.value.getBoundingClientRect();
  const searchBarContainer = props.useSearchBarWidth
    ? (inputEl.value.closest('.search-bar-container') as HTMLElement | null)
    : null;
  const hostRect = props.matchInputWidth
    ? inputRect
    : searchBarContainer?.getBoundingClientRect() ?? inputRect;
  const containerRect = !props.useFixedOverlay
    ? (inputEl.value.closest('.cross-reference') as HTMLElement | null)?.getBoundingClientRect()
    : null;

  const viewportPadding = 16;
  const extraWidth = props.useSearchBarWidth ? 0 : 200;
  const maxWidth = 960;
  const targetWidth =
    (props.matchInputWidth ? hostRect.width : hostRect.width + extraWidth) +
    (props.dropdownExtraWidth || 0);

  // For non-fixed overlays (facets/modal), anchor directly to the local wrapper to avoid viewport misalignment
  if (!props.useFixedOverlay) {
    const overlapOffset = 10;
    const parentEl = inputEl.value.closest('.relative') as HTMLElement | null;
    const parentWidth = parentEl?.clientWidth ?? hostRect.width;

    if (props.overlayPlacement === 'relative') {
      overlayStyle.value = {
        position: 'absolute',
        width: `${parentWidth}px`,
        left: '0',
        top: `${(inputEl.value as HTMLElement).offsetHeight - overlapOffset}px`,
      };
    } else {
      overlayStyle.value = {
        position: 'absolute',
        width: '100%',
        left: '0',
        right: '0',
        top: `${(inputEl.value as HTMLElement).offsetHeight + overlapOffset}px`,
      };
    }
    return;
  }
  const width = Math.min(
    targetWidth,
    maxWidth,
    props.useFixedOverlay ? window.innerWidth - viewportPadding * 2 : targetWidth,
  );
  const extraOffset = props.matchInputWidth ? (props.dropdownExtraWidth || 0) / 2 : 0;
  const baseLeft = props.matchInputWidth
    ? hostRect.left - extraOffset
    : hostRect.left + hostRect.width / 2 - width / 2;
  const left = Math.max(
    baseLeft - (containerRect?.left ?? 0),
    props.useFixedOverlay ? viewportPadding : 0,
  );
  const overlapOffset = 4;
  const top = props.useFixedOverlay
    ? inputRect.bottom - overlapOffset
    : inputRect.bottom - (containerRect?.top ?? 0) - overlapOffset;

  overlayStyle.value = {
    position: props.useFixedOverlay ? 'fixed' : 'absolute',
    width: `${width}px`,
    left: `${left}px`,
    top: `${top}px`,
  };
};

const handleViewportChange = () => {
  if (open.value) {
    positionOverlay();
  }
};

const fetchSuggestions = async (query: string) => {
  if (query.trim().length < minChars) {
    suggestions.value = [];
    open.value = false;
    return;
  }
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  try {
    loading.value = true;
    const results = await apiSuggestStandards(query, abortController, 'name');
    suggestions.value = (results || []).map((item: any) => ({
      id: item.id?.toString() ?? '',
      text: item.value ?? item.label ?? ''
    })).filter((opt: any) => opt.text !== '');
    open.value = suggestions.value.length > 0;
    if (open.value) {
      await nextTick();
      positionOverlay();
    }
  } catch (e: any) {
    if ((e?.code ?? '') === 'ERR_CANCELED') return;
    suggestions.value = [];
    open.value = false;
  } finally {
    loading.value = false;
  }
};

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.value);
  fetchSuggestions(target.value);
};

const onFocus = () => {
  isInputFocused.value = true;
  fetchSuggestions(props.modelValue?.toString() ?? '');
};

const onBlur = () => {
  setTimeout(() => {
    open.value = false;
    isInputFocused.value = false;
  }, 120);
};

const clearInput = () => {
  emit('update:modelValue', '');
  suggestions.value = [];
  open.value = false;
};

const selectItem = (item: SearchFilterOption) => {
  emit('update:modelValue', item.text);
  emit('select', item.text);
  open.value = false;
  suggestions.value = [];
};

watch(() => props.modelValue, (val) => {
  if ((val || '').toString().trim().length < minChars) {
    suggestions.value = [];
    open.value = false;
  }
});

onBeforeUnmount(() => {
  if (abortController) {
    abortController.abort();
  }
});

onMounted(() => {
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('scroll', handleViewportChange, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleViewportChange);
  window.removeEventListener('scroll', handleViewportChange, true);
});
</script>

<template>
  <div class="relative">
    <input
      ref="inputEl"
      type="text"
      :class="[baseInputClass, props.inputClass]"
      :placeholder="placeholder"
      :value="modelValue || ''"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keyup.enter="$emit('select', (modelValue || '').toString())"
    />
    <button
      v-if="(modelValue || '').toString().length > 0"
      type="button"
      class="crossref-clear-btn"
      :class="[clearBtnClass, { 'is-unfocused': !isInputFocused, 'invert-on-blur': props.invertClearOnBlur }]"
      :style="{ top: '50%', transform: 'translateY(-50%)' }"
      @mousedown.prevent
      @click="clearInput"
    >
      <img :src="iconClose" alt="clear" class="h-4 w-4" />
    </button>
    <div v-if="open" class="crossref-overlay" :style="overlayStyle">
      <div class="crossref-overlay__panel">
        <div class="crossref-overlay__list">
          <div v-if="loading" class="crossref-overlay__item crossref-overlay__item--state">
            {{ t('loading') }}...
          </div>
          <div v-else-if="!suggestions.length" class="crossref-overlay__item crossref-overlay__item--state">
            {{ t('noSuggestions') }}
          </div>
          <button
            v-else
            v-for="item in suggestions"
            :key="item.id + item.text"
            type="button"
            class="crossref-overlay__item"
            @mousedown.prevent="selectItem(item)"
          >
            <div class="crossref-overlay__item-icon">
              <img :src="iconDocument" alt="" class="crossref-overlay__doc-img" />
            </div>
            <div class="crossref-overlay__item-text">
              {{ item.text }}
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
